import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useTheme } from '@emotion/react';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import Calendar from '@/components/atoms/Calendar';
import { computeText } from '@/locales/utils';
import { Container, Popover, PresetItem, PresetList, Separator, StyledButton } from './DateRangeSelector.style';

export type DateRange = {
    startDate: string;
    endDate: string;
};

const DATE_PRESETS = [7, 14, 28];

const buildDateRange = (days: number): DateRange => {
    return {
        startDate: dayjs().subtract(days, 'day').startOf('day').toISOString(),
        endDate: dayjs().endOf('day').toISOString(),
    };
};

const formatDateLabel = (startDate: string, endDate: string): string => {
    return `${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`;
};

type DateRangeSelectorProps = {
    selectedPreset: number;
    dateRange: DateRange;
    onChange: (dateRange: DateRange, preset: number) => void;
};

const DateRangeSelector: React.FunctionComponent<DateRangeSelectorProps> = ({
    selectedPreset,
    dateRange,
    onChange,
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => {
        setIsOpen(false);
        setShowCalendar(false);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                close();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, close]);

    const handleToggle = useCallback(() => {
        if (isOpen) {
            close();
        } else {
            setShowCalendar(false);
            setIsOpen(true);
        }
    }, [isOpen, close]);

    const handlePresetClick = useCallback(
        (days: number) => {
            onChange(buildDateRange(days), days);
            close();
        },
        [onChange, close],
    );

    const handleCustomClick = useCallback(() => {
        setShowCalendar(true);
    }, []);

    const handleCalendarChange = useCallback(
        (dates: { startDate: Date | null; endDate: Date | null }) => {
            if (dates.startDate && dates.endDate) {
                onChange(
                    {
                        startDate: dayjs(dates.startDate).startOf('day').toISOString(),
                        endDate: dayjs(dates.endDate).endOf('day').toISOString(),
                    },
                    0,
                );
                close();
            }
        },
        [onChange, close],
    );

    const buttonLabel =
        selectedPreset > 0
            ? intl.formatMessage({ id: 'orderList.filters.dateRange.lastXdays' }, { days: selectedPreset })
            : formatDateLabel(dateRange.startDate, dateRange.endDate);

    return (
        <Container ref={containerRef}>
            <StyledButton onClick={handleToggle}>
                <SVGIcon icon={SVGIcons.CALENDAR} size={16} color={theme.color.bloodyMary} />
                <span>{buttonLabel}</span>
                <SVGIcon icon={SVGIcons.CHEVRON} size={16} color={theme.color.black200} />
            </StyledButton>
            {isOpen && (
                <Popover>
                    {showCalendar ? (
                        <Calendar
                            startDate={new Date(dateRange.startDate)}
                            endDate={new Date(dateRange.endDate)}
                            onChange={handleCalendarChange}
                            selectsRange
                            inline
                            monthShown={2}
                            maxDate={new Date()}
                        />
                    ) : (
                        <PresetList>
                            {DATE_PRESETS.map((days) => (
                                <PresetItem
                                    key={days}
                                    isSelected={selectedPreset === days}
                                    onClick={() => handlePresetClick(days)}
                                >
                                    {intl.formatMessage({ id: 'orderList.filters.dateRange.lastXdays' }, { days })}
                                </PresetItem>
                            ))}
                            <Separator />
                            <PresetItem isSelected={selectedPreset === 0} onClick={handleCustomClick}>
                                {computeText(intl, 'orderList.filters.dateRange.custom')}
                            </PresetItem>
                        </PresetList>
                    )}
                </Popover>
            )}
        </Container>
    );
};

export default DateRangeSelector;
